import qs from "qs";
import { FileService } from "./file.service";
import { Metadata, PropertiesModel } from "../../shared/models/metadata.model";
import { SourceItem } from "../../shared/models/source-item.model";
import { getResult } from "./services.utils";

/***
 * This is the standard backend implementation that uses the API server to get data.
 */
export class FileServiceImpl implements FileService {
    appAPIUrl: string;
    showSnackbar: (message: string, severity: string) => void;

    constructor(appAPIUrl: string, showSnackbar: (message: string, severity: string) => void) {
        this.appAPIUrl = appAPIUrl;
        this.showSnackbar = showSnackbar;
    }

    public getTextfile = async (
        url: string,
    ): Promise<string> => {
        const res = await fetch(url, { credentials: 'include' });
        return res.status === 200 ? res.text() : "";
    }

    public getIdLink = (
        id: string,
    ) => {
        return `${this.appAPIUrl}/file/id/${id}`;
    }

    public getDirectLink = (
        site: string,
        path: string,
        validity: string,
        systemProperties: PropertiesModel,
        lang: string,
        requestItem?: "content" | "metadata" | "markdown",
    ): string => {
        const pathWithLeadingSlash = path.startsWith("/") ? path : "/" + path;
        const urlParams: { [key: string]: unknown } = {};
        if (validity.length) {
            urlParams.v = validity;
        }
        if (Object.keys(systemProperties).length) {
            urlParams.s = systemProperties;
        }
        if (lang.length && requestItem !== "markdown") {
            urlParams.l = lang;
        }
        const urlParamsStr = qs.stringify(urlParams);
        const resourceName = requestItem === "metadata"
            ? "file/metadata-url"
            : requestItem === "markdown"
                ? "markdown"
                : "file";
        let link = `${this.appAPIUrl}/${resourceName}/${site || ''}${pathWithLeadingSlash}`;
        if (urlParamsStr) {
            link += `?${urlParamsStr}`;
        }
        return link;
    }

    public getMarkdownExistsUrl = (
        site: string,
        path: string,
        validity: string,
        systemProperties: PropertiesModel,
    ): string => {
        const pathWithLeadingSlash = path.startsWith("/") ? path : "/" + path;
        const urlParams: { [key: string]: unknown } = {};
        if (validity.length) {
            urlParams.v = validity;
        }
        if (Object.keys(systemProperties).length) {
            urlParams.s = systemProperties;
        }
        const urlParamsStr = qs.stringify(urlParams);
        let link = `${this.appAPIUrl}/markdown/exists/${site || ''}${pathWithLeadingSlash}`;
        if (urlParamsStr) {
            link += `?${urlParamsStr}`;
        }
        return link;
    }

    public getDownloadLink = (
        site: string,
        items: Array<SourceItem>,
        wrapperfile: string | null,
        indexfile: string | null
    ): string => {
        return `${this.appAPIUrl}/download/${site || ''}/?items=${JSON.stringify(items)}${wrapperfile ? '&wrapperfile=' + wrapperfile : ''}${indexfile ? '&indexfile=' + indexfile : ''}`;
    };

    public getMetadata = async (
        id: string,
    ): Promise<Metadata | null> => {
        const res = await fetch(`${this.appAPIUrl}/file/metadata/${id}`, {
            method: "GET",
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        return getResult(res, false, this.showSnackbar);
    }

    public getMetadataByUrl = async (
        url: string,
    ): Promise<Metadata | null> => {
        const res = await fetch(url, {
            method: "GET",
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        return getResult(res, false, this.showSnackbar);
    }

    public getMetadataByToken = async (
        site: string,
        token: string,
    ): Promise<Array<Metadata> | null> => {
        const res = await fetch(`${this.appAPIUrl}/file/metadata/${site}/${token}`, {
            method: "GET",
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        return getResult(res, false, this.showSnackbar);
    }

    public existsByPaths = async (
        site: string,
        paths: string[],
        validity: string,
        lang: string,
    ): Promise<string[]> => {
        const urlParams: { [key: string]: unknown } = {};
        if (validity.length) {
            urlParams.v = validity;
        }
        if (lang.length) {
            urlParams.l = lang;
        }
        const urlParamsStr = qs.stringify(urlParams);
        let url = `${this.appAPIUrl}/file/exists/${site || ""}`;
        if (urlParamsStr) {
            url += `?${urlParamsStr}`;
        }
        const res = await fetch(url, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ paths }),
        });
        const body = await getResult(res, false, this.showSnackbar);
        const existing = body && typeof body === "object" ? (body as { existing?: unknown }).existing : undefined;
        if (!Array.isArray(existing)) {
            throw new Error("File existence check failed");
        }
        return existing.filter((path): path is string => typeof path === "string");
    }

}
